"use client";

import type { NubeSDKRuntimeEvent } from "@tiendanube/nube-sdk-internal-types";
import { useEffect, useRef } from "@tiendanube/nube-sdk-react-runtime";
import type { IframeHTMLAttributes } from "react";
import { useNubeSDK } from "../../hooks/use-nube-sdk";

export type IframeProps = IframeHTMLAttributes<HTMLIFrameElement> & {
	sandbox?: string;
	__internalId?: string;
	onMessage?: boolean;
	autoresize?: boolean;
	allowCamera?: boolean;
	allowMicrophone?: boolean;
};

/**
 * Permissions-Policy features an app can delegate to its iframe.
 *
 * Each feature is gated behind its own explicit opt-in prop, so a raw `allow`
 * string is never forwarded to the DOM. Features are delegated to the iframe's
 * own origin (the default `'src'` allowlist), and only when `src` is HTTPS.
 */
const permissionPolicyFeatures = {
	allowCamera: "camera",
	allowMicrophone: "microphone",
} as const;

type PermissionPolicyProp = keyof typeof permissionPolicyFeatures;

function isHttpsUrl(src: string | undefined): boolean {
	if (!src || typeof src !== "string") {
		return false;
	}

	try {
		return new URL(src).protocol === "https:";
	} catch {
		return false;
	}
}

function buildAllowAttribute(
	src: string | undefined,
	permissions: Partial<Record<PermissionPolicyProp, boolean>>,
): string | undefined {
	if (!isHttpsUrl(src)) {
		return undefined;
	}

	const features = (
		Object.keys(permissionPolicyFeatures) as PermissionPolicyProp[]
	)
		.filter((prop) => permissions[prop] === true)
		.map((prop) => permissionPolicyFeatures[prop]);

	return features.length > 0 ? features.join("; ") : undefined;
}

/**
 * List of allowed sandbox tokens for iframe security.
 *
 * Blocked tokens (not included for security reasons):
 * - 'allow-top-navigation': Allows unrestricted navigation to parent context (security risk)
 * - 'allow-orientation-lock': Allows content to lock screen orientation
 * - 'allow-top-navigation-to-custom-protocols': Allows navigation to custom protocols in top-level context
 */
const allowedSandboxTokens = [
	"allow-forms",
	"allow-scripts",
	"allow-same-origin",
	"allow-popups",
	"allow-pointer-lock",
	"allow-popups-to-escape-sandbox",
	"allow-presentation",
	"allow-top-navigation-by-user-activation",
	"allow-downloads-without-user-activation",
	"allow-downloads",
	"allow-modals",
	"allow-storage-access-by-user-activation",
] as const;

type AllowedSandboxToken = (typeof allowedSandboxTokens)[number];

function validateSandboxTokens(sandbox: string): string {
	if (!sandbox || typeof sandbox !== "string") {
		return "";
	}

	const tokens = sandbox.split(/\s+/).filter(Boolean);
	const valid = tokens.filter((t) =>
		allowedSandboxTokens.includes(t as AllowedSandboxToken),
	);

	return valid.join(" ");
}

type IframeMessageResizeMessage = {
	type: "resize";
	height?: number;
	width?: number;
};

function isIframeMessageResizeMessage(
	message: unknown,
): message is IframeMessageResizeMessage {
	return (
		typeof message === "object" &&
		message !== null &&
		"type" in message &&
		message.type === "resize"
	);
}

function applyIframeMessageResizeMessage(
	element: HTMLIFrameElement,
	message: IframeMessageResizeMessage,
) {
	if (message.height) {
		element.style.height = `${message.height}px`;
	}
	if (message.width) {
		element.style.width = `${message.width}px`;
	}
}

/**
 * Iframe component with type-safe sandbox attribute.
 * Provides a secure wrapper around HTML iframe element with explicit sandbox controls.
 * Automatically filters out any sandbox tokens that are not in the allowed list,
 * and builds the `allow` attribute only from the explicit `allow*` opt-in props.
 */
export function Iframe({
	sandbox,
	__internalId,
	onMessage,
	autoresize = false,
	allow: _ignoredAllow,
	allowCamera,
	allowMicrophone,
	...props
}: IframeProps) {
	const { send } = useNubeSDK("storefront");
	const sandboxAttribute = sandbox ? validateSandboxTokens(sandbox) : undefined;
	const allowAttribute = buildAllowAttribute(props.src, {
		allowCamera,
		allowMicrophone,
	});
	const iframeRef = useRef<HTMLIFrameElement>(null);
	const shouldListenForMessages = __internalId && (onMessage || autoresize);

	useEffect(() => {
		if (!shouldListenForMessages) return;

		const prefix = `custom:iframe:${__internalId}`;
		const handleMessageEvent = (event: MessageEvent) => {
			const iframe = iframeRef.current;
			if (!iframe) {
				return;
			}

			if (event.source !== iframe.contentWindow) {
				return;
			}

			const iframeId = iframe.getAttribute("data-nube-iframe-id");
			if (iframeId !== __internalId) {
				return;
			}

			if (isIframeMessageResizeMessage(event.data)) {
				return applyIframeMessageResizeMessage(iframe, event.data);
			}

			if (onMessage) {
				const modifier = () => ({
					ui: {
						values: {
							[__internalId]: event.data,
						},
					},
				});

				send(`${prefix}:message` as NubeSDKRuntimeEvent, modifier);
			}
		};

		window.addEventListener("message", handleMessageEvent);

		return () => {
			window.removeEventListener("message", handleMessageEvent);
		};
	}, [__internalId, onMessage, autoresize, send]);

	return (
		<iframe
			ref={iframeRef}
			title={`iframe-${__internalId}`}
			id={__internalId}
			{...props}
			sandbox={sandboxAttribute}
			allow={allowAttribute}
			data-nube-iframe-id={__internalId}
		/>
	);
}
